import { useQuery } from '@tanstack/react-query';
import { formatMoney, inboxApi } from '@wa/shared';

/**
 * Templates, as a table.
 *
 * Rates rather than raw counts where a rate is the question — "did people read it"
 * is a proportion, and two templates with 40 and 4,000 sends are not comparable on
 * counts. The counts are there too, because a 100% read rate on five sends is not
 * the same claim as one on five thousand.
 */
export function TemplateTable({ from, to }: { from: string; to: string }) {
  const templates = useQuery({
    queryKey: ['report-templates', from, to],
    queryFn: () => inboxApi.reportTemplates(from, to),
  });

  const rows = templates.data ?? [];
  const rate = (part: number, whole: number) => (whole === 0 ? '—' : `${Math.round((part / whole) * 100)}%`);

  return (
    <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
      <h2 className="border-b border-(--color-line) px-3 py-2.5 text-body font-semibold sm:px-4">
        {rows.length} {rows.length === 1 ? 'template' : 'templates'} sent in this period
      </h2>

      {rows.length === 0 ? (
        <p className="px-3 py-8 text-center text-small text-(--color-muted) sm:px-4">
          No templates have gone out in this range.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] text-small">
            <thead>
              <tr className="border-b border-(--color-line-dim) text-left text-micro uppercase tracking-wide text-(--color-faint)">
                <th className="px-3 py-2 font-medium sm:px-4">Template</th>
                <th className="px-2 py-2 text-right font-medium">Sent</th>
                <th className="px-2 py-2 text-right font-medium">Delivered</th>
                <th className="px-2 py-2 text-right font-medium">Read</th>
                <th className="px-2 py-2 text-right font-medium">Tapped</th>
                <th className="px-3 py-2 text-right font-medium sm:px-4">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-(--color-line-dim)">
              {rows.map((row) => (
                <tr key={row.templateName}>
                  <td className="px-3 py-2 font-mono text-micro sm:px-4">{row.templateName}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{row.sent.toLocaleString('en-IN')}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{rate(row.delivered, row.sent)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{rate(row.readCount, row.sent)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {row.clicked === 0 ? '—' : row.clicked.toLocaleString('en-IN')}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums sm:px-4">₹{formatMoney(row.spend)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="border-t border-(--color-line-dim) px-3 py-2 text-micro text-(--color-muted) sm:px-4">
        Delivered and read come from WhatsApp&rsquo;s own receipts, counted from the messages
        rather than tallied separately &mdash; so a receipt arriving twice cannot inflate them.
      </p>
    </section>
  );
}
